import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import config from '../../config';
import { Users, Search } from 'lucide-react';
import { useSortedPage, SortTh, PageBar } from '../../components/admin/DataTable';
import { formatDateDMY, formatDateTimeDMY } from '../../lib/dates';

function memberLabel(m) {
  return m.full_name || m.name || 'Family member';
}

function FamilyTree({ customer }) {
  const members = customer.family_members || [];
  return (
    <div className="mt-3 pl-3 border-l-2 border-[#673c96]/20" data-testid={`family-tree-${customer.id}`}>
      <div className="relative">
        <p className="text-sm font-semibold text-[#311b52]">{customer.full_name || 'Customer'} <span className="text-[10px] font-normal text-[#9ca3af]">account holder</span></p>
        {members.length === 0 ? (
          <p className="text-xs text-[#9ca3af] mt-1">No family members linked</p>
        ) : (
          <ul className="mt-2 space-y-1">
            {members.map((m) => (
              <li key={m.id} className="flex items-center gap-2 text-sm text-[#311b52]">
                <span className="w-3 h-px bg-[#673c96]/40" />
                <span className="font-medium">{memberLabel(m)}</span>
                <span className="text-xs text-[#673c96]">{m.relation || m.relationship || 'related'}</span>
                {m.age != null && m.age !== '' && <span className="text-xs text-[#9ca3af]">{m.age} yrs</span>}
                {m.date_of_birth && <span className="text-[10px] text-[#9ca3af]">{formatDateDMY(m.date_of_birth)}</span>}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export default function Customers() {
  const { getAuthHeaders } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    const fetch = async () => {
      try {
        const { data } = await axios.get(`${config.API}/admin/customers`, { headers: getAuthHeaders() });
        setCustomers((data.customers || []).map((c) => ({ ...c, family_count: (c.family_members || []).length })));
      } catch (err) { if (process.env.NODE_ENV === 'development') console.error(err); }
      finally { setLoading(false); }
    };
    fetch();
  }, [getAuthHeaders]);

  const filtered = customers.filter((c) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    const phone = String(c.phone || "").replace(/\D/g, "");
    const qDigits = q.replace(/\D/g, "");
    const holder = `${c.full_name || ""} ${c.latest_booking?.customer_name || ""}`.toLowerCase();
    const family = (c.family_members || []).map((m) => `${m.full_name || ""} ${m.name || ""}`).join(" ").toLowerCase();
    const email = String(c.email || "").toLowerCase();
    if (holder.includes(q) || family.includes(q) || email.includes(q)) return true;
    if (qDigits && phone.includes(qDigits)) return true;
    return false;
  });

  const table = useSortedPage(filtered, { defaultKey: 'created_at', defaultDir: 'desc', pageSize: 20 });

  const displayName = (c) => (c.full_name || "").trim() || (c.latest_booking?.customer_name || "").trim() || "Name not added";
  const displayEmail = (c) => {
    const e = String(c.email || "").trim();
    if (!e || e.endsWith("@phone.bookmyjab.local")) return "—";
    return e;
  };

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-2 border-[#673c96] border-t-transparent" /></div>;

  return (
    <div data-testid="admin-customers" className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
        <div className="flex items-center gap-3">
          <Users className="w-5 h-5 text-[#673c96]" />
          <h2 className="text-lg font-bold text-[#311b52]" style={{ fontFamily: 'Poppins' }}>Customers & families</h2>
        </div>
        <label className="relative w-full sm:w-80">
          <Search className="w-4 h-4 text-[#673c96]/50 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search account holder, family member or phone"
            className="w-full pl-9 pr-3 py-2 rounded-full border border-[#673c96]/15 text-sm bg-white"
            data-testid="customers-search"
          />
        </label>
      </div>
      <p className="text-xs text-[#6b7280]">Family members are shown under each customer. Newest accounts first — click a column to sort.</p>

      <div className="bg-white rounded-xl shadow-sm border border-[#673c96]/10 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="customers-table">
            <thead className="bg-[#fdfcff]">
              <tr className="border-b border-[#673c96]/10">
                <SortTh label="Name" k="full_name" sortKey={table.sortKey} sortDir={table.sortDir} onSort={table.toggleSort} />
                <SortTh label="Phone" k="phone" sortKey={table.sortKey} sortDir={table.sortDir} onSort={table.toggleSort} />
                <SortTh label="Email" k="email" sortKey={table.sortKey} sortDir={table.sortDir} onSort={table.toggleSort} />
                <SortTh label="Family" k="family_count" sortKey={table.sortKey} sortDir={table.sortDir} onSort={table.toggleSort} />
                <SortTh label="Bookings" k="booking_count" sortKey={table.sortKey} sortDir={table.sortDir} onSort={table.toggleSort} />
                <SortTh label="Created" k="created_at" sortKey={table.sortKey} sortDir={table.sortDir} onSort={table.toggleSort} />
              </tr>
            </thead>
            <tbody>
              {table.slice.map((c) => {
                const family = c.family_members || [];
                const open = openId === c.id;
                return (
                  <tr key={c.id} className="border-b border-[#673c96]/5 hover:bg-[#fdfcff] transition-colors align-top" data-testid={`customer-row-${c.id}`}>
                    <td className="py-3 px-4">
                      <button type="button" className="font-medium text-[#311b52] text-left" onClick={() => setOpenId(open ? null : c.id)}>
                        {displayName(c)}
                      </button>
                      {(c.account_status === "deleted" || c.deleted_at) && (
                        <span className="ml-2 inline-flex bg-slate-100 text-slate-600 rounded-full px-2 py-0.5 text-[10px] font-semibold">Deleted account</span>
                      )}
                      {open && <FamilyTree customer={c} />}
                    </td>
                    <td className="py-3 px-4 text-[#3e195e]">{c.phone}</td>
                    <td className="py-3 px-4 text-[#673c96]">{displayEmail(c)}</td>
                    <td className="py-3 px-4">
                      <button type="button" className="text-xs font-semibold text-[#673c96]" onClick={() => setOpenId(open ? null : c.id)}>
                        {family.length} linked
                      </button>
                    </td>
                    <td className="py-3 px-4"><span className="bg-[#673c96]/10 text-[#673c96] rounded-full px-2.5 py-0.5 text-xs font-semibold">{c.booking_count}</span></td>
                    <td className="py-3 px-4 text-xs text-[#673c96]">{c.created_at ? formatDateTimeDMY(c.created_at) : '—'}</td>
                  </tr>
                );
              })}
              {customers.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-sm text-[#673c96]">No customers found</td></tr>}
            </tbody>
          </table>
        </div>
        <PageBar page={table.page} pages={table.pages} total={table.total} setPage={table.setPage} noun="customers" />
      </div>
    </div>
  );
}
